import {
  Alert,
  Badge,
  Button,
  Group,
  List,
  NumberInput,
  PasswordInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconSend } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { cas } from './skrbnistvo';
import { PoslanaEposta } from './PoslanaEposta';
import { glave, obNapaki } from './zeton';

type Nastavitve = Shema['EpostaNastavitve'];

const KLJUC = ['skrbnik', 'eposta'];

const VARNOST = [
  { value: 'STARTTLS', label: 'STARTTLS (vrata 587)' },
  { value: 'SSL', label: 'SSL/TLS (vrata 465)' },
  { value: 'BREZ', label: 'Brez šifriranja' },
];

/**
 * Skrbništvo → E-pošta: strežnik SMTP za registracijo, pozabljeno geslo in obvestila (Gmail relay domene
 * lunalabs.solutions z dostopom po IP strežnika), pošiljatelj, naslovi za obvestila, preizkusno sporočilo in evidenca
 * vseh poslanih sporočil ({@link PoslanaEposta}).
 */
export function EpostaSkrbnistvo() {
  const nastavitve = useQuery({
    queryKey: KLJUC,
    queryFn: async ({ signal }) => {
      try {
        return await podatki(api.GET('/api/skrbnik/eposta', { signal, headers: glave() }));
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  if (!nastavitve.data) {
    return (
      <Kartica>
        <StanjeNalaganja
          napaka={nastavitve.error}
          ponovi={() => void nastavitve.refetch()}
          naslov="Nastavitev e-pošte ni bilo mogoče naložiti"
        />
      </Kartica>
    );
  }
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <NastavitveEposte key={nastavitve.data.spremenjeno ?? 0} n={nastavitve.data} />
        <Stack gap="md">
          <Preizkus />
          <Kartica naslov="Gmail SMTP relay">
            <List type="ordered" spacing="xs" size="sm">
              <List.Item>
                Google Admin → <b>Apps → Google Workspace → Gmail → Routing → SMTP relay service</b>: dovoljeni
                pošiljatelji <b>Only addresses in my domains</b>, preverjanje{' '}
                <b>Only accept mail from the specified IP addresses</b> (javni IP strežnika),{' '}
                <b>Require TLS encryption</b>.
              </List.Item>
              <List.Item>
                Strežnik <b>smtp-relay.gmail.com</b>, vrata 587, STARTTLS, uporabnik in geslo prazna. Ime v EHLO naj bo
                naša domena (<b>lunalabs.solutions</b>) – relay sicer zavrne povezavo.
              </List.Item>
              <List.Item>Pošiljatelj mora biti naslov naše domene, nato pošljite preizkusno sporočilo.</List.Item>
            </List>
          </Kartica>
        </Stack>
      </SimpleGrid>
      <PoslanaEposta />
    </Stack>
  );
}

function NastavitveEposte({ n }: { n: Nastavitve }) {
  const odjemalec = useQueryClient();
  const [urejeno, setUrejeno] = useState({
    streznik: n.streznik ?? '',
    vrata: (n.vrata ?? 587) as number | string,
    varnost: n.varnost ?? 'STARTTLS',
    uporabnik: n.uporabnik ?? '',
    geslo: '',
    ehlo: n.ehlo ?? '',
    posiljatelj: n.posiljatelj ?? '',
    imePosiljatelja: n.imePosiljatelja ?? '',
    obvestila: n.obvestila ?? '',
    odgovori: n.odgovori ?? '',
  });
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const polje = (k: Exclude<keyof typeof urejeno, 'vrata' | 'varnost'>) => ({
    value: urejeno[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setUrejeno({ ...urejeno, [k]: e.currentTarget.value }),
    error: napake[k],
  });

  const shrani = async () => {
    setDela(true);
    setNapake({});
    setNapaka(null);
    try {
      const novo = await podatki(
        api.PUT('/api/skrbnik/eposta', {
          headers: glave(),
          body: { ...urejeno, vrata: urejeno.vrata === '' ? undefined : Number(urejeno.vrata) },
        }),
      );
      odjemalec.setQueryData(KLJUC, novo);
      notifications.show({ color: 'green', message: 'Nastavitve e-pošte so shranjene.' });
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  return (
    <Kartica
      naslov="Pošiljanje e-pošte"
      dejanja={
        n.nastavljeno ? (
          <Badge color="green" variant="light">
            nastavljeno
          </Badge>
        ) : (
          <Badge color="gray" variant="light">
            ni nastavljeno
          </Badge>
        )
      }
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Za potrditev registracije, pozabljeno geslo, obvestila skrbnikom in okrožnice. Brez strežnika in pošiljatelja
          se e-pošta ne pošilja.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput label="Strežnik SMTP" placeholder="smtp-relay.gmail.com" {...polje('streznik')} />
          <NumberInput
            size="md"
            label="Vrata"
            value={urejeno.vrata}
            onChange={(v) => setUrejeno({ ...urejeno, vrata: v })}
            min={1}
            max={65535}
            allowDecimal={false}
            hideControls
            error={napake.vrata}
          />
          <Select
            label="Šifriranje"
            data={VARNOST}
            value={urejeno.varnost}
            onChange={(v) => setUrejeno({ ...urejeno, varnost: v ?? 'STARTTLS' })}
            allowDeselect={false}
            error={napake.varnost}
          />
          <TextInput
            label="Ime v EHLO"
            description="Domena, s katero se strežnik predstavi"
            placeholder="lunalabs.solutions"
            {...polje('ehlo')}
          />
          <TextInput
            label="Uporabnik"
            description="Prazno = brez prijave (dostop po IP)"
            autoComplete="off"
            {...polje('uporabnik')}
          />
          <PasswordInput
            label="Geslo"
            description={n.gesloVpisano ? 'Vpisano – vpišite samo, če ga želite zamenjati' : 'Samo s prijavo'}
            placeholder={n.gesloVpisano ? '••••••••' : ''}
            autoComplete="new-password"
            disabled={!urejeno.uporabnik.trim()}
            {...polje('geslo')}
          />
          <TextInput label="Pošiljatelj" placeholder="info@lunalabs.solutions" {...polje('posiljatelj')} />
          <TextInput label="Ime pošiljatelja" placeholder="Avto Pisarna" {...polje('imePosiljatelja')} />
        </SimpleGrid>
        <TextInput
          label="Naslov za odgovore"
          description="Kamor gredo odgovori na sistemska sporočila in okrožnice (Reply-To); prazno = pošiljatelju"
          placeholder="info@lunalabs.solutions"
          {...polje('odgovori')}
        />
        <TextInput
          label="Obvestila skrbnikom"
          description="Naslovi za obvestila o novih registracijah, ločeni z vejico"
          placeholder="info@lunalabs.solutions, matjaz@lunalabs.solutions"
          {...polje('obvestila')}
        />
        {napaka && (
          <Alert color="red" variant="light" role="alert">
            {napaka}
          </Alert>
        )}
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            {n.spremenil ? `Nazadnje spremenil ${n.spremenil}, ${cas(n.spremenjeno)}` : ''}
          </Text>
          <Button onClick={() => void shrani()} loading={dela}>
            Shrani
          </Button>
        </Group>
      </Stack>
    </Kartica>
  );
}

function Preizkus() {
  const odjemalec = useQueryClient();
  const [prejemnik, setPrejemnik] = useState('');
  const [napakaPolja, setNapakaPolja] = useState<string | undefined>();
  const [rezultat, setRezultat] = useState<{ uspelo: boolean; besedilo: string } | null>(null);
  const [dela, setDela] = useState(false);

  const poslji = async () => {
    setDela(true);
    setNapakaPolja(undefined);
    setRezultat(null);
    try {
      await izvedi(api.POST('/api/skrbnik/eposta/preizkus', { headers: glave(), body: { prejemnik } }));
      setRezultat({ uspelo: true, besedilo: `Sporočilo je poslano na ${prejemnik.trim()}. Preverite prejeto pošto.` });
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && e.polja.prejemnik) {
        setNapakaPolja(e.polja.prejemnik);
      } else {
        setRezultat({ uspelo: false, besedilo: sporocilo(e) });
      }
    } finally {
      setDela(false);
      // tudi neuspešen poskus je v evidenci poslanih
      void odjemalec.invalidateQueries({ queryKey: ['skrbnik', 'eposta-poslana'] });
    }
  };

  return (
    <Kartica naslov="Preizkusno sporočilo">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Pošlje sporočilo s shranjenimi nastavitvami. Če ne uspe, je prikazan odgovor strežnika SMTP.
        </Text>
        <TextInput
          label="Prejemnik"
          placeholder="ime@lunalabs.solutions"
          value={prejemnik}
          onChange={(e) => setPrejemnik(e.currentTarget.value)}
          error={napakaPolja}
        />
        {rezultat && (
          <Alert
            color={rezultat.uspelo ? 'green' : 'red'}
            variant="light"
            role={rezultat.uspelo ? undefined : 'alert'}
            icon={rezultat.uspelo ? <IconCheck size={18} /> : undefined}
          >
            {rezultat.besedilo}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" leftSection={<IconSend size={18} />} onClick={() => void poslji()} loading={dela}>
            Pošlji preizkusno
          </Button>
        </Group>
      </Stack>
    </Kartica>
  );
}
